import type { ContextMenu, MapDispatchMenu } from './droneMapTypes';
import { MAP_CONTEXT_MENU } from '@/lib/copy';

interface DroneMapContextMenuProps {
  contextMenu: ContextMenu | null;
  mapDispatchMenu: MapDispatchMenu | null;
  onRequestDispatchHere?: (latitude: number, longitude: number) => void;
  onCloseDispatchMenu: () => void;
  onCloseContextMenu: () => void;
  onContextAction: (action: string) => void;
  onOpenVideo?: (droneId: string) => void;
  hasTrail: boolean;
  hasCtrlLink: boolean;
  followDroneId: string | null;
  /**
   * Range rings, for the aircraft this menu was opened on.
   *
   * `shown` is narrower than the map's `showRangeRings` flag on purpose: the
   * rings are drawn for the SELECTED airborne aircraft only
   * (`rangeRingAnchor`), so on a menu that can be opened on any aircraft the
   * honest state is "on, AND about this one". `drawable` is false when this
   * aircraft is parked or its link is LOST, which is a momentary block rather
   * than a structural one (D13) - so the item stays, disabled, and says why.
   */
  rangeRings: { shown: boolean; drawable: boolean } | null;
}

export function DroneMapContextMenu({
  contextMenu,
  mapDispatchMenu,
  onRequestDispatchHere,
  onCloseDispatchMenu,
  onCloseContextMenu,
  onContextAction,
  onOpenVideo,
  hasTrail,
  hasCtrlLink,
  followDroneId,
  rangeRings,
}: DroneMapContextMenuProps) {
  // Both panels below are `surface-chrome` (92% popover), NOT `glass-card`.
  // `--glass-bg` is a ~5% wash: it composited predictably over a flat vector
  // basemap and composites to BLURRED PHOTOGRAPHY now that imagery is the
  // default ground. `MapLayersMenu`, `CopDockRoster` and `DroneHoverChip` each
  // reached that conclusion separately, and this is a surface an operator acts
  // on under time pressure - it dispatches an aircraft.
  return (
    <>
      {mapDispatchMenu && onRequestDispatchHere && (
        <div
          className="absolute z-50 surface-chrome rounded-lg py-1 min-w-[200px] text-sm"
          style={{ left: mapDispatchMenu.x, top: mapDispatchMenu.y }}
        >
          <div className="px-3 py-1.5 text-[10px] text-muted-foreground uppercase tracking-wider border-b divider">
            Map location
          </div>
          <button
            type="button"
            onClick={() => {
              onRequestDispatchHere(mapDispatchMenu.lat, mapDispatchMenu.lng);
              onCloseDispatchMenu();
            }}
            className="w-full text-left px-3 py-2 text-muted-foreground row-hover hover:text-foreground transition-colors"
          >
            Dispatch drone here…
          </button>
        </div>
      )}

      {contextMenu && (
        <div
          role="menu"
          tabIndex={0}
          className="absolute z-50 surface-chrome rounded-lg py-1 min-w-[180px] text-sm outline-none"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onKeyDown={(e) => { if (e.key === 'Escape') onCloseContextMenu(); }}
        >
          <div className="px-3 py-1.5 text-[10px] text-muted-foreground uppercase tracking-wider border-b divider">
            {contextMenu.callsign}
          </div>
          {onOpenVideo && (
            <button
              role="menuitem"
              type="button"
              onClick={() => onContextAction('video')}
              className="w-full text-left px-3 py-2 text-muted-foreground row-hover hover:text-foreground transition-colors"
            >
              {MAP_CONTEXT_MENU.camera}
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => onContextAction('center')} className="w-full text-left px-3 py-2 text-muted-foreground row-hover hover:text-foreground transition-colors">
            {MAP_CONTEXT_MENU.center}
          </button>
          <button type="button" role="menuitem" onClick={() => onContextAction('follow')} className="w-full text-left px-3 py-2 text-muted-foreground row-hover hover:text-foreground transition-colors">
            {followDroneId === contextMenu.droneId ? MAP_CONTEXT_MENU.stopFollowing : MAP_CONTEXT_MENU.follow}
          </button>
          <button type="button" role="menuitem" onClick={() => onContextAction('trail')} className="w-full text-left px-3 py-2 text-muted-foreground row-hover hover:text-foreground transition-colors">
            {hasTrail ? MAP_CONTEXT_MENU.hideTrack : MAP_CONTEXT_MENU.showTrack}
          </button>
          <button type="button" role="menuitem" onClick={() => onContextAction('controller')} className="w-full text-left px-3 py-2 text-muted-foreground row-hover hover:text-foreground transition-colors">
            {hasCtrlLink ? MAP_CONTEXT_MENU.hideControllerLink : MAP_CONTEXT_MENU.showControllerLink}
          </button>
          {rangeRings && (
            <button
              type="button"
              role="menuitem"
              disabled={!rangeRings.drawable && !rangeRings.shown}
              title={rangeRings.drawable ? undefined : MAP_CONTEXT_MENU.rangeRingsNeedAir}
              onClick={() => onContextAction('rangeRings')}
              className="w-full text-left px-3 py-2 text-muted-foreground row-hover hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-muted-foreground"
            >
              {rangeRings.shown ? MAP_CONTEXT_MENU.hideRangeRings : MAP_CONTEXT_MENU.showRangeRings}
            </button>
          )}
        </div>
      )}
    </>
  );
}
